import {
  Component,
  Inject,
  PLATFORM_ID,
  signal,
  DestroyRef,
  inject,
  computed,
} from '@angular/core';
import { LottieAnimationComponent } from '@shared/components/lottie-animation/lottie-animation.component';
import { UpdateRequiredDialogComponent } from '@shared/components/update-required-dialog/update-required-dialog.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Location } from '@angular/common';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { LayoutService } from '@shared/services/layout/layout.service';
import { LanguageService } from '@core/services/language/language.service';
import { App } from '@capacitor/app';
import { GoogleOneTapService } from '@shared/services/google-one-tap/google-one-tap.service';
import { UtmService } from '@core/services/utm/utm.service';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import { PlatformService } from '@core/services/platform/platform.service';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { resolveScreenName } from '@shared/utils/screen-name.util';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    CommonModule,
    ToastModule,
    LottieAnimationComponent,
    UpdateRequiredDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  isPlatFormBrowser = false;
  splashState = signal<'visible' | 'hidden' | 'off'>('off');
  currentUrl = signal('');
  orientation = signal<'rtl' | 'ltr'>('rtl');
  private destroyRef = inject(DestroyRef);

  splashSrc = computed(
    () => 'assets/animations/STOQA_Logo_Animation_Horizontal_Black.lottie' as const
  );

  isRTL(): boolean {
    return this.orientation() === 'rtl';
  }

  isLTR(): boolean {
    return this.orientation() === 'ltr';
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private layoutService: LayoutService,
    private router: Router,
    private messageService: MessageService,
    private languageService: LanguageService,
    private location: Location,
    private googleOneTapService: GoogleOneTapService,
    private utmService: UtmService,
    private analyticsService: AnalyticsService,
    private platformService: PlatformService,
    private authenticationService: AuthenticationService
  ) {}

  ngOnInit() {
    this.isPlatFormBrowser = isPlatformBrowser(this.platformId);
    if (this.isPlatFormBrowser) {
      this.utmService.capture();
      this.initSplash();

      // Clear chunk error flags on successful startup
      sessionStorage.removeItem('chunk-reload-ts');
      sessionStorage.removeItem('chunk-reload-count');

      this.currentUrl.set(window.location.pathname);
      this.orientation.set(this.languageService.getTextDirection());

      // Subscribe to language changes to update orientation
      this.languageService
        .onLanguageChange()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.orientation.set(this.languageService.getTextDirection());
        });
    }

    // Toast notifications
    this.layoutService.$notifyToaster.subscribe((message: any) => {
      let severity = 'info';
      if (message.type === 'success') {
        severity = 'success';
      } else if (message.type === 'error') {
        severity = 'error';
      } else if (message.type === 'info') {
        severity = 'info';
      }
      this.messageService.add({
        severity: severity,
        detail: message.text,
      });
    });
    if (this.isPlatFormBrowser) {
      if (window.google?.accounts) {
        this.googleOneTapService.initialize();
      } else {
        window.addEventListener('load', () => this.googleOneTapService.initialize(), {
          once: true,
        });
      }
    }

    // Track current URL for back button handler
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.url);
      }
    });

    // Handle hardware back button for mobile
    this.setupBackButtonHandler();
    this.setupDeepLinkHandler();
    this.setupAppOpenedTracking();
    this.setupScreenViewTracking();
  }

  private lastFiredScreenPath: string | null = null;

  private setupScreenViewTracking(): void {
    this.router.events.subscribe((event: any) => {
      if (!(event instanceof NavigationEnd)) return;
      // Some screens (e.g. company page tabs) reflect internal state via query params,
      // which still triggers NavigationEnd - only re-fire on a real path change, not a
      // query-param-only navigation within the same screen.
      const path = event.urlAfterRedirects.split('?')[0];
      if (path === this.lastFiredScreenPath) return;
      this.lastFiredScreenPath = path;
      const screenName = resolveScreenName(this.router);
      if (!screenName) return;
      this.analyticsService.trackScreenViewed({
        screenName,
        currentPlan: this.authenticationService.currentUser()?.subscription?.subscriptionType,
      });
    });
  }

  private setupAppOpenedTracking(): void {
    // app_opened is native-app-only per spec (xlsx Event Map tags it "Client (app)",
    // not the default "Client" = web+app - see Wave 3a plan's Global Constraints).
    // The web build never registers this listener or fires the event at all.
    if (!this.platformService.isMobile()) return;

    // ngOnInit runs once per app process - this call is the cold start.
    this.analyticsService.trackAppOpened({ isColdStart: true });

    // Any later 'active' transition means the app was backgrounded and resumed
    // within the same process - a warm start, not a cold one.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.analyticsService.trackAppOpened({ isColdStart: false });
      }
    });
  }

  private initSplash(): void {
    if (sessionStorage.getItem('splash-shown')) return;

    this.splashState.set('visible');
    sessionStorage.setItem('splash-shown', '1');
    setTimeout(() => {
      this.splashState.set('hidden');
      setTimeout(() => this.splashState.set('off'), 500);
    }, 2000);
  }

  private setupDeepLinkHandler(): void {
    App.addListener('appUrlOpen', (data: { url: string }) => {
      const url = new URL(data.url);
      // Universal/App Link (https://stoqa.ai/company/2222) already has the real
      // path as pathname. Custom scheme (stoqa://payment/moyasar-callback?id=xxx)
      // parses its first segment as the hostname, so it must be re-prepended.
      const isUniversalLink = url.hostname === 'stoqa.ai';
      const path = isUniversalLink
        ? url.pathname + url.search + url.hash
        : '/' + url.hostname + url.pathname + url.search + url.hash;
      this.analyticsService.trackDeepLinkOpened({
        path,
        linkType: isUniversalLink ? 'universal_link' : 'custom_scheme',
      });
      this.router.navigateByUrl(path);
    });
  }

  private setupBackButtonHandler() {
    App.addListener('backButton', ({ canGoBack }) => {
      const currentUrl = this.currentUrl();
      const rootPages = ['/', '/home', '/landing-page'];
      const isRootPage = rootPages.includes(currentUrl);

      if (!canGoBack || isRootPage) {
        App.exitApp();
      } else {
        this.location.back();
      }
    });
  }
}
